import { t, Instance, flow, SnapshotIn } from 'mobx-state-tree';
import { PersonModel, type PersonType } from './models/Person';
import { RelationshipModel } from './models/Relationship';
import { api } from '@/api';
import { generateUUID } from '@/utils/uuid';
import type {
  AddFamilyMemberRequest,
  AdditionalParentRequest,
  AddPetOwnerRequest,
  CreatePersonRequest,
  CreatePetRequest,
  CreateRelationshipRequest,
  PersonResponse,
  PetResponse,
  RelationshipResponse,
  UpdatePersonRequest,
  UpdatePetRequest
} from 'we-tree-api-client/v2';
import type { LivingStatusType } from 'we-tree-api-client/v2';
import { PetOwnerRoleType } from 'we-tree-api-client/v2';
import { DEFAULT_GENDER } from '@/types';
import { domainStore } from './DomainStore';

/** Person create: server still requires client-supplied IDs; generated inside the store action, not in UI. */
export type CreatePersonPayload = Omit<CreatePersonRequest, 'personId' | 'familyMemberId'>;

export type CreateRelationshipPayload = Omit<CreateRelationshipRequest, 'id'>;

export type AdditionalParentPayload = Omit<AdditionalParentRequest, 'relationshipId'>;

export type AddFamilyMemberPayload = Omit<
  AddFamilyMemberRequest,
  'personId' | 'familyMemberId' | 'relationshipId' | 'additionalParents'
> & {
  additionalParents?: AdditionalParentPayload[];
};

export type CreatePetPayload = Omit<CreatePetRequest, 'familyMemberId'>;

/**
 * FamilyStore - Single family graph data
 * Contains complete people and relationships for one family
 * Cleared when switching families
 */
export const FamilyStore = t
  .model('FamilyStore', {
    // Family identifier
    familyId: t.string,

    // Graph data - Maps for O(1) lookups by ID
    // Note: Family metadata (name, memberCount, etc.) is stored in DomainStore.familiesMetadata
    people: t.map(PersonModel),
    relationships: t.map(RelationshipModel),
    pets: t.map(t.frozen<PetResponse>()),

    // Loading state
    isLoaded: t.optional(t.boolean, false),
    isLoading: t.optional(t.boolean, false),
    lastLoadedAt: t.maybeNull(t.Date),
    lastServerUpdatedAt: t.maybeNull(t.Date) // Server's updatedAt timestamp from when we last loaded
  })
  .views((self) => ({
    // Basic graph traversal views
    // Note: familyMemberId is used as the key since people are stored by their FAMILY_MEMBER.ID
    getPerson(familyMemberId: string) {
      return self.people.get(familyMemberId);
    },

    /**
     * Real people only — excludes pedigree scaffolding placeholders (never persisted / never exported).
     */
    get exportablePeople(): PersonType[] {
      return Array.from(self.people.values()).filter((person) => !person.isPlaceholder);
    },

    getPet(familyMemberId: string): PetResponse | undefined {
      return self.pets.get(familyMemberId);
    },

    /** Whether an API pet payload lists the given family member as an owner (e.g. link-existing picker). */
    petResponseHasOwner(pet: PetResponse, ownerFamilyMemberId: string): boolean {
      return (pet.owners ?? []).some((o) => o.ownerFamilyMemberId === ownerFamilyMemberId);
    },

    /** Whether an API pet payload already has a primary owner (drives role options when linking). */
    petResponseHasPrimaryOwner(pet: PetResponse): boolean {
      return (pet.owners ?? []).some((o) => o.role === PetOwnerRoleType.Primary);
    },

    // Relationship traversal views
    getSpouses(familyMemberId: string): PersonType[] {
      const spouses: PersonType[] = [];
      self.relationships.forEach((rel) => {
        if (rel.relationshipType === 'spousal') {
          if (rel.familyMember1Id === familyMemberId) {
            const spouse = self.people.get(rel.familyMember2Id);
            if (spouse) spouses.push(spouse);
          } else if (rel.familyMember2Id === familyMemberId) {
            const spouse = self.people.get(rel.familyMember1Id);
            if (spouse) spouses.push(spouse);
          }
        }
      });
      return spouses;
    },

    getChildren(familyMemberId: string): PersonType[] {
      const children: PersonType[] = [];
      self.relationships.forEach((rel) => {
        if (rel.isParentChild && rel.familyMember1Id === familyMemberId) {
          const child = self.people.get(rel.familyMember2Id);
          if (child) children.push(child);
        }
      });
      return children;
    },

    /**
     * Check if a person has any known (non-placeholder) children
     * Used to determine if a placeholder person can be selected as TreeAnchor
     * (since they may have known children that should be viewable)
     */
    hasKnownChildren(familyMemberId: string): boolean {
      const children = this.getChildren(familyMemberId);
      return children.some((child) => !child.isPlaceholder);
    },

    getParents(familyMemberId: string): PersonType[] {
      const parents: PersonType[] = [];
      self.relationships.forEach((rel) => {
        if (rel.isParentChild && rel.familyMember2Id === familyMemberId) {
          const parent = self.people.get(rel.familyMember1Id);
          if (parent) parents.push(parent);
        }
      });
      return parents;
    },

    /**
     * Get parent relationships with metadata (for editing relationship types).
     * Returns parents of the given member (member is the child).
     */
    getParentRelationships(
      familyMemberId: string
    ): { parent: PersonType; relationship: Instance<typeof RelationshipModel> }[] {
      const result: { parent: PersonType; relationship: Instance<typeof RelationshipModel> }[] = [];
      self.relationships.forEach((rel) => {
        if (rel.isParentChild && rel.familyMember2Id === familyMemberId) {
          const parent = self.people.get(rel.familyMember1Id);
          if (parent) result.push({ parent, relationship: rel });
        }
      });
      return result;
    },

    /**
     * Get child relationships with metadata (for editing relationship types).
     * Returns children of the given member (member is the parent).
     */
    getChildRelationships(
      familyMemberId: string
    ): { child: PersonType; relationship: Instance<typeof RelationshipModel> }[] {
      const result: { child: PersonType; relationship: Instance<typeof RelationshipModel> }[] = [];
      self.relationships.forEach((rel) => {
        if (rel.isParentChild && rel.familyMember1Id === familyMemberId) {
          const child = self.people.get(rel.familyMember2Id);
          if (child) result.push({ child, relationship: rel });
        }
      });
      return result;
    },

    /**
     * Spousal relationships for the given member (for profile editing / removal).
     */
    getSpouseRelationships(
      familyMemberId: string
    ): { spouse: PersonType; relationship: Instance<typeof RelationshipModel> }[] {
      const result: { spouse: PersonType; relationship: Instance<typeof RelationshipModel> }[] = [];
      self.relationships.forEach((rel) => {
        if (rel.relationshipType === 'spousal') {
          if (rel.familyMember1Id === familyMemberId) {
            const spouse = self.people.get(rel.familyMember2Id);
            if (spouse) result.push({ spouse, relationship: rel });
          } else if (rel.familyMember2Id === familyMemberId) {
            const spouse = self.people.get(rel.familyMember1Id);
            if (spouse) result.push({ spouse, relationship: rel });
          }
        }
      });
      return result;
    },

    memberHasRelationships(familyMemberId: string): boolean {
      for (const rel of self.relationships.values()) {
        if (rel.familyMember1Id === familyMemberId || rel.familyMember2Id === familyMemberId) {
          return true;
        }
      }
      for (const pet of self.pets.values()) {
        if (pet.owners?.some((owner) => owner.ownerFamilyMemberId === familyMemberId)) {
          return true;
        }
      }
      return false;
    },

    getProfileRelationshipCount(familyMemberId: string): number {
      const graphCount =
        this.getParentRelationships(familyMemberId).length +
        this.getSpouseRelationships(familyMemberId).length +
        this.getChildRelationships(familyMemberId).length;
      let ownedPetCount = 0;
      self.pets.forEach((pet) => {
        if (pet.owners?.some((owner) => owner.ownerFamilyMemberId === familyMemberId)) {
          ownedPetCount += 1;
        }
      });
      return graphCount + ownedPetCount;
    },

    /**
     * Get siblings of a person (people who share the same parents)
     */
    getSiblings(familyMemberId: string): PersonType[] {
      const siblings: PersonType[] = [];
      const parents = this.getParents(familyMemberId);

      // Get all parent IDs
      const parentIds = parents.map((p) => p.familyMemberId);

      if (parentIds.length === 0) {
        return siblings; // No parents means no siblings
      }

      // Find all people who have the same parents
      self.relationships.forEach((rel) => {
        if (rel.isParentChild && parentIds.includes(rel.familyMember1Id)) {
          const sibling = self.people.get(rel.familyMember2Id);
          // Exclude self and placeholders
          if (sibling && sibling.familyMemberId !== familyMemberId && !sibling.isPlaceholder) {
            // Check if this sibling has all the same parents
            const siblingParents = this.getParents(sibling.familyMemberId);
            const siblingParentIds = siblingParents.map((p) => p.familyMemberId);
            if (
              parentIds.length === siblingParentIds.length &&
              parentIds.every((id) => siblingParentIds.includes(id))
            ) {
              siblings.push(sibling);
            }
          }
        }
      });

      return siblings;
    },

    /**
     * Gets the root person (oldest ancestor with earliest birth date)
     * Finds people with no parents, then selects the one with the earliest birth date
     * Returns the familyMemberId of the root person, or null if no people exist
     */
    getRootPersonFamilyMemberId(): string | null {
      if (self.people.size === 0) {
        return null;
      }

      // Find all people with no parents (root ancestors)
      const rootAncestors: PersonType[] = [];
      self.people.forEach((person) => {
        // Check if person has parents by looking for parent-child relationships
        const hasParents = Array.from(self.relationships.values()).some(
          (rel) => rel.isParentChild && rel.familyMember2Id === person.familyMemberId
        );
        if (!hasParents) {
          rootAncestors.push(person);
        }
      });

      // If no root ancestors found (everyone has parents), return first person as fallback
      if (rootAncestors.length === 0) {
        const firstPerson = Array.from(self.people.values())[0];
        return firstPerson ? firstPerson.familyMemberId : null;
      }

      // If only one root ancestor, return it
      if (rootAncestors.length === 1) {
        return rootAncestors[0].familyMemberId;
      }

      // Find the one with the earliest birth date
      let earliestPerson: PersonType | null = null;
      let earliestBirthDate: Date | null = null;

      for (const person of rootAncestors) {
        if (person.birthDate) {
          const birthDate = new Date(person.birthDate);
          if (!earliestBirthDate || birthDate < earliestBirthDate) {
            earliestBirthDate = birthDate;
            earliestPerson = person;
          }
        }
      }

      // If we found someone with a birth date, return them
      if (earliestPerson) {
        return earliestPerson.familyMemberId;
      }

      // If no one has a birth date, return the first root ancestor
      return rootAncestors[0].familyMemberId;
    },

    /**
     * Gets the family member ID for a user by their personId
     * Returns the familyMemberId if found, or null if not found
     */
    getFamilyMemberIdByPersonId(personId: string): string | null {
      let foundFamilyMemberId: string | null = null;

      self.people.forEach((person) => {
        if (person.personId === personId) {
          foundFamilyMemberId = person.familyMemberId;
        }
      });

      return foundFamilyMemberId;
    },

    /**
     * Gets the family member ID for a user by their userId
     * Returns the familyMemberId if found, or null if not found
     */
    getFamilyMemberIdByUserId(userId: string): string | null {
      let foundFamilyMemberId: string | null = null;

      self.people.forEach((person) => {
        if (person.userId === userId) {
          foundFamilyMemberId = person.familyMemberId;
        }
      });

      return foundFamilyMemberId;
    },

    /**
     * Resolves the signed-in user's family member in this family.
     * Tries personId first (account or post-claim), then userId on the member record.
     */
    getFamilyMemberIdForUser(
      userPersonId: string | null | undefined,
      userId: string | null | undefined
    ): string | null {
      if (userPersonId) {
        const byPersonId = this.getFamilyMemberIdByPersonId(userPersonId);
        if (byPersonId) return byPersonId;
      }
      if (userId) {
        return this.getFamilyMemberIdByUserId(userId);
      }
      return null;
    }
  }))
  .actions((self) => ({
    // Basic actions for populating the store
    clear() {
      self.people.clear();
      self.relationships.clear();
      self.pets.clear();
      self.isLoaded = false;
      self.lastLoadedAt = null;
      self.lastServerUpdatedAt = null;
    },

    addPerson(personData: PersonType | SnapshotIn<typeof PersonModel>) {
      const person = PersonModel.create(personData);
      self.people.set(personData.familyMemberId, person);
    },

    addRelationship(relData: Instance<typeof RelationshipModel>) {
      const relationship = RelationshipModel.create(relData);
      self.relationships.set(relData.id, relationship);
    },

    // API integration
    loadFamilyData: flow(function* (familyId: string, force: boolean = false) {
      // If force is true, skip version check and always reload
      if (!force && self.familyId === familyId && self.isLoaded) {
        // Check if recently loaded (< 1 minute) to prevent rapid-fire reloads
        const recentlyLoaded = self.lastLoadedAt && Date.now() - self.lastLoadedAt.getTime() < 60 * 1000;

        // If recently loaded, check version to see if we can skip
        // If not recently loaded, always check version to ensure freshness
        try {
          const versionResult = yield api.families.getFamilyVersion(familyId);

          if (versionResult.success && versionResult.data) {
            const serverUpdatedAt = new Date(versionResult.data.updatedAt);
            const lastServerUpdated = self.lastServerUpdatedAt;

            // Compare server's current updatedAt with server's updatedAt from when we last loaded
            if (lastServerUpdated && serverUpdatedAt.getTime() <= lastServerUpdated.getTime()) {
              return; // Data is fresh, skip reload
            }
            // Data has changed, continue with reload below
          } else {
            // Version check failed - reload anyway (safe default)
            // eslint-disable-next-line no-console
            console.warn('[FamilyStore] Version check failed, reloading anyway', {
              familyId,
              error: versionResult.error?.getUserMessage(),
              recentlyLoaded
            });
            // Continue with reload below
          }
        } catch (error) {
          // Version check error - reload anyway (safe default)
          // eslint-disable-next-line no-console
          console.warn('[FamilyStore] Version check error, reloading anyway', {
            familyId,
            error: error instanceof Error ? error.message : String(error),
            recentlyLoaded
          });
          // Continue with reload below
        }
      }

      // If already loading the same family, wait for it to complete
      if (self.isLoading && self.familyId === familyId) {
        // Wait for the in-progress load to complete by polling
        while (self.isLoading && self.familyId === familyId) {
          yield new Promise((resolve) => setTimeout(resolve, 50)); // Wait 50ms and check again
        }
        return; // Load completed, return without doing anything
      }

      // If loading a different family, clear and start fresh
      // This handles the case where user switches families while a load is in progress
      self.familyId = familyId;
      self.people.clear();
      self.relationships.clear();
      self.pets.clear();
      self.isLoaded = false;
      self.isLoading = true;
      self.lastLoadedAt = null;
      self.lastServerUpdatedAt = null;

      try {
        // Load family metadata first
        const familyResult = yield api.families.getFamily(familyId);

        let familyResponse = null;
        if (familyResult.success) {
          familyResponse = familyResult.data;
          // Sync metadata to DomainStore (single source of truth)
          domainStore.updateFamilyMetadata({
            id: familyId,
            name: familyResponse.name,
            displayName: familyResponse.displayName ?? familyResponse.name,
            alternativeName: familyResponse.alternativeName ?? null,
            plan: familyResponse.plan || 'free',
            memberCount: familyResponse.memberCount || 0,
            createdAt: familyResponse.createdAt,
            updatedAt: familyResponse.updatedAt,
            createdBy: familyResponse.createdBy || null,
            createdByNickname: familyResponse.createdByNickname || null,
            updatedBy: familyResponse.updatedBy || null,
            updatedByNickname: familyResponse.updatedByNickname || null,
            lastActivityAt: familyResponse.lastActivityAt || familyResponse.createdAt || '',
            lastActivityBy: familyResponse.lastActivityBy ?? null,
            userRole: familyResponse.userRole || null,
            canDeleteFamily: familyResponse.canDeleteFamily ?? false
          });
        } else {
          // Handle typed errors with better logging
          // eslint-disable-next-line no-console
          console.error('[FamilyStore] Failed to get family:', familyResult.error.getUserMessage(), {
            familyId,
            errorCode: familyResult.error.code,
            errorDetails: familyResult.error.details
          });

          // For auth errors, we might want to handle differently
          if (familyResult.error.isAuthError()) {
            // eslint-disable-next-line no-console
            console.warn('[FamilyStore] Authentication error loading family - user may need to re-login');
          }
        }

        // Load all family members using the new bulk endpoint
        const membersResult = yield api.families.getFamilyMembers(familyId);

        let membersResponse = null;
        if (membersResult.success) {
          membersResponse = membersResult.data;
        } else {
          // Handle typed errors for family members
          // eslint-disable-next-line no-console
          console.error('[FamilyStore] Failed to get family members:', membersResult.error.getUserMessage(), {
            familyId,
            errorCode: membersResult.error.code,
            errorDetails: membersResult.error.details
          });

          // For some errors, we might still want to continue loading relationships
          if (!membersResult.error.isAuthError()) {
            // eslint-disable-next-line no-console
            console.warn('[FamilyStore] Continuing with relationships load despite members error');
          }
        }

        // Check if familyId changed during the API call (user switched families)
        // If so, ignore the results and don't update the store
        if (self.familyId !== familyId) {
          return;
        }

        let peopleLoaded = 0;
        let peopleSkipped = 0;

        if (membersResponse?.members) {
          membersResponse.members.forEach((person: PersonResponse) => {
            // Skip people with missing familyMemberId or personId (required for identification)
            if (!person.familyMemberId || !person.personId) {
              // eslint-disable-next-line no-console
              console.warn('[FamilyStore] Skipping person with missing familyMemberId or personId:', {
                person,
                hasFamilyMemberId: !!person.familyMemberId,
                hasPersonId: !!person.personId
              });
              peopleSkipped++;
              return;
            }

            // Use givenName if available, otherwise fall back to displayName or surname
            const firstName = person.givenName || person.surname || 'Unknown';
            const lastName = person.givenName ? person.surname || '' : '';

            const personData = {
              familyMemberId: person.familyMemberId,
              personId: person.personId, // Add the actual person ID
              userId: person.userId || null,
              firstName: firstName,
              lastName: lastName,
              middleName: person.middleName || null,
              birthDate: person.birthDate || null,
              birthPlace: person.birthPlace || null,
              deathDate: person.deathDate || null,
              livingStatus: (person as PersonResponse).livingStatus ?? 'unknown',
              gender: person.gender || DEFAULT_GENDER,
              photoUrl: (person as PersonResponse & { photoUrl?: string | null }).photoUrl ?? null,
              updatedAt: person.updatedAt ?? ''
            };
            self.people.set(personData.familyMemberId, PersonModel.create(personData));
            peopleLoaded++;
          });
        } else {
          // eslint-disable-next-line no-console
          console.warn('[FamilyStore] No members in response', { membersResponse });
        }

        // Load all relationships using the new bulk endpoint
        const relationshipsResult = yield api.families.getFamilyRelationships(familyId);

        let relationshipsResponse = null;
        if (relationshipsResult.success) {
          relationshipsResponse = relationshipsResult.data;
        } else {
          // Handle typed errors for family relationships
          // eslint-disable-next-line no-console
          console.error(
            '[FamilyStore] Failed to get family relationships:',
            relationshipsResult.error.getUserMessage(),
            {
              familyId,
              errorCode: relationshipsResult.error.code,
              errorDetails: relationshipsResult.error.details
            }
          );

          // Relationships are optional - we can still show people without relationships
          // eslint-disable-next-line no-console
          console.warn('[FamilyStore] Continuing without relationships data');
        }

        // Check again if familyId changed during the API call
        if (self.familyId !== familyId) {
          return;
        }

        if (relationshipsResponse?.relationships) {
          relationshipsResponse.relationships.forEach((rel: RelationshipResponse) => {
            const relData = {
              id: rel.id,
              familyMember1Id: rel.familyMember1Id,
              familyMember2Id: rel.familyMember2Id,
              relationshipType: rel.relationshipType,
              notes: rel.notes || null,
              startDate: rel.effectiveFrom || null,
              endDate: rel.effectiveTo || null
            };
            self.relationships.set(relData.id, RelationshipModel.create(relData));
          });
        } else {
          // eslint-disable-next-line no-console
          console.warn('[FamilyStore] No relationships in response', { relationshipsResponse });
        }

        // Load pets for this family (mirrors people loading)
        const petsResult = yield api.pets.searchPets(familyId);
        if (self.familyId !== familyId) {
          return;
        }
        if (petsResult.success && petsResult.data?.pets) {
          petsResult.data.pets.forEach((pet: PetResponse) => {
            if (pet.familyMemberId) {
              self.pets.set(pet.familyMemberId, pet);
            }
          });
        }

        // Populate spouseFamilyMemberIds for each person based on spousal relationships
        self.relationships.forEach((rel) => {
          if (rel.relationshipType === 'spousal') {
            const person1 = self.people.get(rel.familyMember1Id);
            const person2 = self.people.get(rel.familyMember2Id);

            if (person1 && person2) {
              // Add spouse to each person's spouseFamilyMemberIds array if not already present
              if (!person1.spouseFamilyMemberIds.includes(rel.familyMember2Id)) {
                person1.addSpouseFamilyMemberId(rel.familyMember2Id);
              }
              if (!person2.spouseFamilyMemberIds.includes(rel.familyMember1Id)) {
                person2.addSpouseFamilyMemberId(rel.familyMember1Id);
              }
            }
          }
        });

        // Final check: ensure familyId hasn't changed before marking as loaded
        if (self.familyId !== familyId) {
          return;
        }

        // Mark as loaded if we successfully loaded the family metadata
        // Even if there are no people, the family metadata should be available
        if (familyResponse) {
          self.isLoaded = true;
          self.lastLoadedAt = new Date();
          // Store the server's updatedAt timestamp for future version comparisons
          self.lastServerUpdatedAt = new Date(familyResponse.updatedAt);

          if (peopleLoaded === 0) {
            // eslint-disable-next-line no-console
            console.warn('[FamilyStore] Family metadata loaded, but no people found', {
              familyId,
              peopleLoaded,
              peopleSkipped,
              actualPeopleSize: self.people.size,
              membersResponse: membersResponse ? 'present' : 'missing',
              membersArrayLength: membersResponse?.members?.length ?? 0,
              hasMembers: !!membersResponse?.members,
              membersIsArray: Array.isArray(membersResponse?.members),
              membersResponseKeys: membersResponse ? Object.keys(membersResponse) : []
            });
          }
        } else {
          // eslint-disable-next-line no-console
          console.error('[FamilyStore] Failed to load family metadata', {
            familyId,
            familyResponse: familyResponse ? 'present' : 'missing'
          });
          // Keep isLoaded as false if metadata failed to load
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('[FamilyStore] Error loading family data', {
          familyId,
          error,
          errorMessage: error instanceof Error ? error.message : String(error)
        });
        // Keep isLoaded as false on error
        throw error;
      } finally {
        self.isLoading = false;
      }
    }),

    /**
     * Update a person by family member ID
     * Handles optimistic updates and rollback on error
     */
    updatePerson: flow(function* (familyMemberId: string, personData: UpdatePersonRequest) {
      const person = self.people.get(familyMemberId);
      if (!person) {
        return { success: false, error: new Error('Person not found in store') };
      }

      // Save original data for rollback
      const originalData = {
        firstName: person.firstName,
        lastName: person.lastName,
        middleName: person.middleName,
        birthDate: person.birthDate,
        birthPlace: person.birthPlace,
        deathDate: person.deathDate,
        livingStatus: person.livingStatus as LivingStatusType,
        gender: person.gender,
        familyMemberId: person.familyMemberId,
        personId: person.personId,
        userId: person.userId,
        spouseFamilyMemberIds: Array.from(person.spouseFamilyMemberIds),
        lastSelectedSpouseFamilyMemberId: person.lastSelectedSpouseFamilyMemberId,
        isPlaceholder: person.isPlaceholder,
        positionMetadata: person.positionMetadata
      };

      // Optimistic update: update the store immediately
      person.updatePerson({
        firstName: personData.givenName || person.firstName,
        lastName: personData.surname || person.lastName,
        middleName: personData.middleName ?? person.middleName,
        birthDate: personData.birthDate || person.birthDate,
        birthPlace: personData.birthPlace !== undefined ? personData.birthPlace || null : person.birthPlace,
        deathDate: personData.deathDate || person.deathDate,
        livingStatus: (personData.livingStatus ?? person.livingStatus) as LivingStatusType,
        gender: personData.gender || person.gender,
        familyMemberId: person.familyMemberId,
        personId: person.personId,
        userId: person.userId,
        spouseFamilyMemberIds: Array.from(person.spouseFamilyMemberIds),
        lastSelectedSpouseFamilyMemberId: person.lastSelectedSpouseFamilyMemberId,
        isPlaceholder: person.isPlaceholder,
        positionMetadata: person.positionMetadata
      });

      try {
        const result = yield api.people.updatePerson(familyMemberId, personData);

        if (result.success) {
          // Update person in store with server response data
          const updatedPerson = self.people.get(familyMemberId);
          if (updatedPerson && result.data) {
            updatedPerson.updatePerson({
              firstName: result.data.givenName || updatedPerson.firstName,
              lastName: result.data.surname || updatedPerson.lastName,
              middleName: result.data.middleName ?? updatedPerson.middleName,
              birthDate: result.data.birthDate || updatedPerson.birthDate,
              birthPlace: result.data.birthPlace ?? updatedPerson.birthPlace,
              deathDate: result.data.deathDate || updatedPerson.deathDate,
              livingStatus: (result.data.livingStatus ?? updatedPerson.livingStatus) as LivingStatusType,
              gender: result.data.gender || updatedPerson.gender,
              familyMemberId: updatedPerson.familyMemberId,
              personId: updatedPerson.personId,
              userId: result.data.userId || updatedPerson.userId,
              spouseFamilyMemberIds: Array.from(updatedPerson.spouseFamilyMemberIds),
              lastSelectedSpouseFamilyMemberId: updatedPerson.lastSelectedSpouseFamilyMemberId,
              isPlaceholder: updatedPerson.isPlaceholder,
              positionMetadata: updatedPerson.positionMetadata
            });
          }
          return { success: true, data: result.data };
        } else {
          // Rollback optimistic update
          person.updatePerson(originalData);
          return { success: false, error: result.error };
        }
      } catch (error) {
        // Rollback optimistic update
        person.updatePerson(originalData);
        return {
          success: false,
          error: error instanceof Error ? error : new Error('Failed to update person')
        };
      }
    }),

    /**
     * Fetch a person by family member ID from the API
     * Always loads from API to ensure we have complete PersonResponse data
     * (PersonModel in store doesn't have all PersonResponse fields like birthPlace, deathPlace, etc.)
     */
    fetchPerson: flow(function* (familyMemberId: string) {
      try {
        const result = yield api.people.getPerson(familyMemberId);
        if (result.success && result.data) {
          // Update store with basic person data if person exists in store
          const existingPerson = self.people.get(familyMemberId);
          if (existingPerson) {
            existingPerson.updatePerson({
              firstName: result.data.givenName || existingPerson.firstName,
              lastName: result.data.surname || existingPerson.lastName,
              middleName: result.data.middleName ?? existingPerson.middleName,
              birthDate: result.data.birthDate || existingPerson.birthDate,
              birthPlace: result.data.birthPlace ?? existingPerson.birthPlace,
              deathDate: result.data.deathDate || existingPerson.deathDate,
              livingStatus: (result.data.livingStatus ?? existingPerson.livingStatus) as LivingStatusType,
              gender: result.data.gender || existingPerson.gender,
              familyMemberId: existingPerson.familyMemberId,
              personId: existingPerson.personId,
              userId: result.data.userId || existingPerson.userId,
              spouseFamilyMemberIds: Array.from(existingPerson.spouseFamilyMemberIds),
              lastSelectedSpouseFamilyMemberId: existingPerson.lastSelectedSpouseFamilyMemberId,
              isPlaceholder: existingPerson.isPlaceholder,
              positionMetadata: existingPerson.positionMetadata
            });
          }
        }
        return result;
      } catch (error) {
        return {
          success: false,
          error: error instanceof Error ? error : new Error('Failed to get person')
        };
      }
    }),

    /**
     * Claim a profile by associating a user with a person record
     */
    claimProfile: flow(function* (familyMemberId: string) {
      try {
        const result = yield api.people.claimProfile(familyMemberId);

        if (result.success) {
          // Update the person in store with the new userId
          const person = self.people.get(familyMemberId);
          if (person && result.data) {
            person.updatePerson({
              firstName: person.firstName,
              lastName: person.lastName,
              middleName: person.middleName,
              birthDate: person.birthDate,
              birthPlace: person.birthPlace,
              deathDate: person.deathDate,
              livingStatus: person.livingStatus as LivingStatusType,
              gender: person.gender,
              familyMemberId: person.familyMemberId,
              personId: person.personId,
              userId: result.data.userId || null,
              spouseFamilyMemberIds: Array.from(person.spouseFamilyMemberIds),
              lastSelectedSpouseFamilyMemberId: person.lastSelectedSpouseFamilyMemberId,
              isPlaceholder: person.isPlaceholder,
              positionMetadata: person.positionMetadata
            });
          }
          return result;
        } else {
          return result;
        }
      } catch (error) {
        return {
          success: false,
          error: error instanceof Error ? error : new Error('Failed to claim profile')
        };
      }
    }),

    /**
     * Fetch a pet by family member ID from the API
     * Mirrors fetchPerson: loads full PetResponse from API and updates store
     */
    fetchPet: flow(function* (familyMemberId: string) {
      try {
        const result = yield api.pets.getPet(familyMemberId);
        if (result.success && result.data) {
          self.pets.set(familyMemberId, result.data);
        }
        return result;
      } catch (error) {
        return {
          success: false,
          error: error instanceof Error ? error : new Error('Failed to get pet')
        };
      }
    }),

    /**
     * Update a pet by family member ID
     * Mirrors updatePerson: optimistic update and rollback on error
     */
    updatePet: flow(function* (familyMemberId: string, petData: UpdatePetRequest) {
      const existingPet = self.pets.get(familyMemberId);
      if (!existingPet) {
        return { success: false, error: new Error('Pet not found in store') };
      }

      const originalPet = { ...existingPet };

      const optimisticPet: PetResponse = {
        ...existingPet,
        ...petData,
        displayName: petData.displayName ?? existingPet.displayName,
        category: petData.category ?? existingPet.category,
        subType: petData.subType ?? existingPet.subType,
        sex: petData.sex ?? existingPet.sex,
        birthDate: petData.birthDate ?? existingPet.birthDate,
        birthDatePrecision: petData.birthDatePrecision ?? existingPet.birthDatePrecision,
        adoptionDate: petData.adoptionDate ?? existingPet.adoptionDate,
        adoptionDatePrecision: petData.adoptionDatePrecision ?? existingPet.adoptionDatePrecision,
        deathDate: petData.deathDate ?? existingPet.deathDate,
        deathDatePrecision: petData.deathDatePrecision ?? existingPet.deathDatePrecision
      };
      self.pets.set(familyMemberId, optimisticPet);

      try {
        const result = yield api.pets.updatePet(familyMemberId, petData);

        if (result.success && result.data) {
          self.pets.set(familyMemberId, result.data);
          return { success: true, data: result.data };
        } else {
          self.pets.set(familyMemberId, originalPet);
          return { success: false, error: result.error };
        }
      } catch (error) {
        self.pets.set(familyMemberId, originalPet);
        return {
          success: false,
          error: error instanceof Error ? error : new Error('Failed to update pet')
        };
      }
    }),

    /** Create a standalone person in the family (e.g. add-self flow). Assigns personId and familyMemberId. */
    createPerson: flow(function* (body: CreatePersonPayload) {
      try {
        const request: CreatePersonRequest = {
          ...body,
          personId: generateUUID(),
          familyMemberId: generateUUID()
        };
        return yield api.people.createPerson(request);
      } catch (error) {
        return {
          success: false as const,
          error: error instanceof Error ? error : new Error('Failed to create person')
        };
      }
    }),

    /** Add a new person linked as child, parent, or spouse. Assigns person, member, relationship, and additional-parent IDs. */
    addFamilyMember: flow(function* (body: AddFamilyMemberPayload) {
      try {
        const { additionalParents: apIn, ...rest } = body;
        const additionalParents =
          apIn && apIn.length > 0 ? apIn.map((ap) => ({ ...ap, relationshipId: generateUUID() })) : undefined;
        const request: AddFamilyMemberRequest = {
          ...rest,
          personId: generateUUID(),
          familyMemberId: generateUUID(),
          relationshipId: generateUUID(),
          ...(additionalParents ? { additionalParents } : {})
        };
        return yield api.people.addFamilyMember(request);
      } catch (error) {
        return {
          success: false as const,
          error: error instanceof Error ? error : new Error('Failed to add family member')
        };
      }
    }),

    /** Create a relationship between existing members (e.g. link spouse / parent–child). Assigns relationship id. */
    createRelationship: flow(function* (body: CreateRelationshipPayload) {
      try {
        const request: CreateRelationshipRequest = {
          ...body,
          id: generateUUID()
        };
        return yield api.relationships.createRelationship(request);
      } catch (error) {
        return {
          success: false as const,
          error: error instanceof Error ? error : new Error('Failed to create relationship')
        };
      }
    }),

    /** List pets in the family (e.g. link-existing picker). Does not mutate store. */
    searchPets: flow(function* (familyId: string) {
      try {
        return yield api.pets.searchPets(familyId);
      } catch (error) {
        return {
          success: false as const,
          error: error instanceof Error ? error : new Error('Failed to search pets')
        };
      }
    }),

    createPet: flow(function* (body: CreatePetPayload) {
      try {
        const request: CreatePetRequest = {
          ...body,
          familyMemberId: generateUUID()
        };
        return yield api.pets.createPet(request);
      } catch (error) {
        return {
          success: false as const,
          error: error instanceof Error ? error : new Error('Failed to create pet')
        };
      }
    }),

    addPetOwner: flow(function* (petFamilyMemberId: string, body: AddPetOwnerRequest) {
      try {
        return yield api.pets.addPetOwner(petFamilyMemberId, body);
      } catch (error) {
        return {
          success: false as const,
          error: error instanceof Error ? error : new Error('Failed to add pet owner')
        };
      }
    })
  }));

export type FamilyStoreType = Instance<typeof FamilyStore>;

// Export singleton instance
export const familyStore = FamilyStore.create({
  familyId: '',
  isLoaded: false
});
